/** variants.redirect_url is varchar(1024). */
export const REDIRECT_URL_MAX_LENGTH = 1024;

/** The control every experiment is created with. The dashboard still identifies it by this name. */
export const ORIGINAL_VARIANT_NAME = 'Original';

export const REDIRECT_URL_SCHEME_MESSAGE = 'Start the address with https:// (or http://).';

export const ORIGINAL_NAME_TAKEN = `${ORIGINAL_VARIANT_NAME} is added for you. Name this variant after what it changes.`;

export const DESTINATION_REQUIRED_MESSAGE = 'A URL Redirect variant needs a destination URL';

// Tabs and newlines are stripped during URL parsing, so a scheme broken across a
// newline would pass the check while the browser still honours it.
const hasControlCharacters = (value: string): boolean => {
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (code < 32 || code === 127) return true;
  }
  return false;
};

/**
 * Why a redirect destination cannot be stored, or undefined when it can.
 *
 * The runtime assigns it straight to `window.location.href`, so anything that is
 * not an absolute http(s) URL misbehaves on the customer's site: `shop.com/x`
 * resolves as a path relative to the page the visitor is on (every visitor in the
 * variant lands on a 404), and `javascript:` runs as code. Shared by the API, which
 * refuses to store one, and the dashboard, which explains it before Save.
 *
 * An empty value is not a problem here: a variant without a destination is
 * incomplete, not invalid, and the Start guard is what refuses to run it.
 */
export function redirectUrlProblem(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (value.length > REDIRECT_URL_MAX_LENGTH) {
    return `Keep the address under ${REDIRECT_URL_MAX_LENGTH} characters.`;
  }
  if (hasControlCharacters(value)) return REDIRECT_URL_SCHEME_MESSAGE;

  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:' ? undefined : REDIRECT_URL_SCHEME_MESSAGE;
  } catch {
    return REDIRECT_URL_SCHEME_MESSAGE;
  }
}

/**
 * True for a name that would make a second Original. Every experiment is created
 * with one, and the dashboard recognises the control by this name, so a duplicate
 * is locked against rename and treated as the control wherever the name is read.
 */
export const isOriginalVariantName = (name: string | null | undefined): boolean =>
  (name ?? '').trim().toLowerCase() === ORIGINAL_VARIANT_NAME.toLowerCase();

type DestinationParams = { name: string | null; redirectUrl: string | null };

/**
 * A URL Redirect test variant with nowhere to send visitors. Original is the page
 * as it is, so it never needs one; the rest cannot start without one.
 */
export const isMissingDestination = ({ name, redirectUrl }: DestinationParams): boolean =>
  !isOriginalVariantName(name) && !redirectUrl?.trim();

/**
 * What holds a URL Redirect experiment back from starting: a test variant with no
 * destination, or one stored before destinations were validated that the runtime
 * would mishandle. Shared by the dashboard's Start button and the start endpoint.
 */
export const lacksUsableDestination = (variant: DestinationParams): boolean =>
  isMissingDestination(variant) || !!redirectUrlProblem(variant.redirectUrl?.trim());
